import type { RecordingId } from "@repo/app-types/entities/recording";
import type { AccountGradingRecording } from "@repo/app-types/entities/teammatePerformanceSummary";
import {
  type RefObject,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AudioPlayerContext } from "../../../components/audioPlayer/context.js";
import type { TranscriptPosition } from "../transcriptNavigationHelpers.js";
import {
  calculateTimeFromCharacterOffsets,
  createSnippetUrl,
  estimateCharacterOffsets,
  findFragmentData,
  maybeGetShareSnippetFromUrl,
} from "./snippetSharingHelpers.js";

export interface FocusedSnippetState {
  recordingId: RecordingId;
  startFragmentIndex: number;
  endFragmentIndex: number;
  startCharOffset?: number;
  endCharOffset?: number;
}

export interface SnippetShareState {
  startSec: number;
  endSec: number;
  url: string;
  onClose: () => void;
}

/**
 * Hook that enables transcript snippet sharing functionality on a transcript.
 * Handles:
 * - Parsing snippet from the URL and displaying it and seeking to the correct audio position
 * - Text selection: Converting user text selections into shareable snippets
 *
 * Requires the transcript fragments to have data-fragment-index and data-recording-id attributes.
 *
 * @param params.containerRef - Reference to the container element holding transcript fragments
 * @param params.recordings - Array of recording objects with transcript data
 * @returns activeSnippet (currently highlighted snippet) and snippetToShare (share UI state)
 */
export function useTranscriptSnippetSharing(params: {
  containerRef: RefObject<HTMLElement | null>;
  recordings: readonly AccountGradingRecording[];
  /** Text targets can scroll and highlight without selecting call audio. */
  seek?: (sourceKey: string, time: number) => void;
  scrollToTranscriptPosition: (position: TranscriptPosition) => void;
}) {
  const { containerRef, recordings, scrollToTranscriptPosition } = params;
  const audioPlayer = useContext(AudioPlayerContext);
  const seek = params.seek ?? audioPlayer.seek;

  const recordingsById = useMemo(
    () => new Map(recordings.map((r) => [r.id, r])),
    [recordings],
  );

  const [activeSnippet, setActiveSnippet] = useState<
    FocusedSnippetState | undefined
  >();
  const [snippetToShare, setSnippetToShare] = useState<
    SnippetShareState | undefined
  >();

  // The snippet moves the playhead once; after that the playhead is the reader's, including when this view is shown again
  const didSeekToSnippetRef = useRef(audioPlayer.hasRequestedPosition);

  // Check if there's a snippet in the URL that should be parsed and displayed
  useEffect(() => {
    const target = maybeGetShareSnippetFromUrl();

    if (!target) return;

    const recording = recordingsById.get(target.recordingId);
    if (!recording) return;

    // Calculate playback position and seek audio
    const { startSec } = calculateTimeFromCharacterOffsets(
      recording,
      target.startFragmentIndex,
      target.endFragmentIndex,
      target.startCharOffset,
      target.endCharOffset,
    );

    if (!didSeekToSnippetRef.current) {
      didSeekToSnippetRef.current = true;
      seek(target.recordingId, startSec);
      scrollToTranscriptPosition({
        type: "call",
        recordingId: target.recordingId,
        timestamp: startSec * 1000,
        fragmentIndex: target.startFragmentIndex,
      });
    }

    // Set active snippet state (FocusedSnippetState fields map 1:1)
    setActiveSnippet({ ...target });
  }, [seek, recordingsById, scrollToTranscriptPosition]);

  // Handle selection changes to detect when user selects text, and show the share overlay
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let isDragging = false;

    const handleMouseDown = () => {
      isDragging = true;
    };

    const handleMouseUp = () => {
      isDragging = false;
      // Small delay to allow selection to settle after drag
      setTimeout(() => {
        if (!isDragging) {
          handleSelectionChange();
        }
      }, 10);
    };

    const handleSelectionChange = () => {
      // Don't show popover while actively dragging
      if (isDragging) return;

      const selection = window.getSelection();

      // Clear overlay if no selection
      if (!selection || selection.isCollapsed) {
        setSnippetToShare(undefined);
        return;
      }

      // Check if selection is within our container
      const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
      if (!range || !container.contains(range.commonAncestorContainer)) {
        setSnippetToShare(undefined);
        return;
      }

      // Triple-click can end at the next speaker's label. Use only fragments
      // whose text is actually selected, ignoring trailing paragraph breaks.
      const selectedFragments: { element: Element; text: string }[] = [];
      for (const fragment of container.querySelectorAll(
        "[data-recording-id][data-fragment-index]",
      )) {
        if (!range.intersectsNode(fragment)) continue;

        const selectedText = document.createRange();
        selectedText.selectNodeContents(fragment);
        if (
          range.compareBoundaryPoints(Range.START_TO_START, selectedText) > 0
        ) {
          selectedText.setStart(range.startContainer, range.startOffset);
        }
        if (range.compareBoundaryPoints(Range.END_TO_END, selectedText) < 0) {
          selectedText.setEnd(range.endContainer, range.endOffset);
        }
        const text = selectedText.toString();
        if (text.trim()) selectedFragments.push({ element: fragment, text });
      }
      const startSelectionFragmentData = findFragmentData(
        selectedFragments.at(0)?.element ?? null,
        container,
      );
      const endSelectionFragmentData = findFragmentData(
        selectedFragments.at(-1)?.element ?? null,
        container,
      );

      // Validate selection is within same recording
      if (
        !startSelectionFragmentData ||
        !endSelectionFragmentData ||
        startSelectionFragmentData.recordingId !==
          endSelectionFragmentData.recordingId
      ) {
        setSnippetToShare(undefined);
        return;
      }

      // Determine fragment order (user's selection may have been from later to newer fragment)
      const [startIdx, endIdx] =
        startSelectionFragmentData.fragmentIndex <=
        endSelectionFragmentData.fragmentIndex
          ? [
              startSelectionFragmentData.fragmentIndex,
              endSelectionFragmentData.fragmentIndex,
            ]
          : [
              endSelectionFragmentData.fragmentIndex,
              startSelectionFragmentData.fragmentIndex,
            ];

      // Get fragment texts and calculate character offsets from selection
      const recording = recordingsById.get(
        startSelectionFragmentData.recordingId,
      );
      if (!recording) {
        setSnippetToShare(undefined);
        return;
      }
      const fragments = recording.transcript?.fragments ?? [];
      const startFragmentText = fragments[startIdx]?.text_transcript ?? "";
      const endFragmentText = fragments[endIdx]?.text_transcript ?? "";

      const selectionText = selectedFragments
        .map(({ text }) => text)
        .join("\n");
      const { start_offset, end_offset } = estimateCharacterOffsets(
        selectionText,
        startFragmentText,
        endFragmentText,
        startIdx === endIdx,
      );

      // Calculate time bounds
      const { startSec, endSec } = calculateTimeFromCharacterOffsets(
        recording,
        startIdx,
        endIdx,
        start_offset,
        end_offset,
      );

      // Generate sharing URL
      const url = createSnippetUrl({
        recordingId: startSelectionFragmentData.recordingId,
        startFragmentIndex: startIdx,
        endFragmentIndex: endIdx,
        startCharOffset: start_offset,
        endCharOffset: end_offset,
      });

      // Set complete overlay state
      setSnippetToShare({
        startSec,
        endSec,
        url,
        onClose: () => {
          window.getSelection()?.removeAllRanges();
        },
      });
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    container.addEventListener("mousedown", handleMouseDown);
    container.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
      container.removeEventListener("mousedown", handleMouseDown);
      container.removeEventListener("mouseup", handleMouseUp);
    };
  }, [containerRef, recordingsById]);

  return { activeSnippet, snippetToShare };
}
